export type Platform = 'meta' | 'tiktok' | 'spotify' | 'google'

export type CampaignObjective = 'LEAD_GENERATION' | 'OUTCOME_TRAFFIC'
export type CampaignStatus = 'NONE' | 'PAUSED' | 'ACTIVE'
export type SpecialAdCategory = 'NONE' | 'EMPLOYMENT' | 'HOUSING' | 'CREDIT' | 'ELECTIONS_POLITICS'

export type PerformanceGoal = 'IMPRESSIONS' | 'LINK_CLICKS'
export type BidStrategy = 'LOWEST_COST_WITHOUT_CAP' | 'COST_CAP' | 'LOWEST_COST_WITH_BID_CAP'
export type ContextCardStyle = 'LIST_STYLE' | 'PARAGRAPH_STYLE'
export type DestinationType = 'ON_AD' | 'WEBSITE'
export type BudgetType = 'daily_budget' | 'lifetime_budget'
export type Placement = 'ADVANTAGE_PLUS' | 'MANUAL'

export type CreativeType = 'SINGLE_IMAGE' | 'SINGLE_VIDEO' | 'CAROUSEL'
export type CtaType = 'APPLY_NOW' | 'LEARN_MORE' | 'SIGN_UP' | 'SUBSCRIBE' | 'GET_QUOTE'

export type QuestionType =
  | 'FULL_NAME'
  | 'EMAIL'
  | 'PHONE_NUMBER'
  | 'CITY'
  | 'STATE'
  | 'JOB_TITLE'
  | 'COMPANY_NAME'
  | 'JOB_ROLE'
  | 'CUSTOM'

export interface ContactQuestion {
  id: string
  key?: string // for custom questions: the API key (lowercase with underscores)
  type: QuestionType
  required: boolean
  isCustom?: boolean
  label?: string
}

export interface TargetingCity {
  key: string
  name: string
  region?: string
  countryCode?: string
  radius: number // in kilometers
}

export interface CampaignSetupFormValues {
  // Platform
  platforms: Platform[]
  adAccountId: string
  socialPageId: string

  // Campaign
  campaignName: string
  campaignObjective: CampaignObjective
  campaignStatus: CampaignStatus
  specialAdCategory: SpecialAdCategory

  // Ad Set
  adSetName: string
  performanceGoal: PerformanceGoal
  adSetStatus: CampaignStatus
  destinationType: DestinationType
  startTime: string | null
  budgetType: BudgetType
  endTime: string | null
  amount: number | ''
  targetingCities: TargetingCity[]
  targetingAgeMin: number
  targetingAgeMax: number
  placements: Placement
  bidStrategy: BidStrategy

  // Media
  mediaAssets: SocialMediaAsset[]

  // Lead Form
  leadFormName: string
  privacyPolicyUrl: string
  introHeadline: string
  introDescription: string
  contextCardStyle: ContextCardStyle
  questionPageCustomHeadline: string
  contactQuestions: ContactQuestion[]
  thankYouTitle: string
  thankYouButtonUrl: string
  thankYouButtonText: string
  thankYouBody: string

  // Ad Creative
  adName: string
  creativeType: CreativeType
  primaryText: string
  headline: string
  description: string
  websiteUrl: string
  ctaType: CtaType
}

export interface AdAccountOption {
  id: string
  name: string
  externalId: string
}

export interface SocialPageOption {
  id: string
  name: string
  externalId: string
  instagramActorId?: string | null
}

export type MediaPlacement = 'feed_square' | 'feed_portrait' | 'story'

export const PLACEMENT_LABELS: Record<MediaPlacement, string> = {
  feed_square: 'Feed (Square)',
  feed_portrait: 'Feed (Portrait)',
  story: 'Story / Reels',
}

export const PLACEMENT_COLORS: Record<MediaPlacement, string> = {
  feed_square: '#1877f2',
  feed_portrait: '#0d7a5f',
  story: '#7c3aed',
}

// Accepted image aspect ratios: [ratio, placement, label]. Meta specifies images by ratio
// plus a minimum size, not by exact pixel dimensions — its own recommended feed resolution
// (1440x1800) is larger than the 1080x1350 this used to demand, so matching exact sizes
// rejected perfectly valid uploads. Matched like the video ratios below.
export const ACCEPTED_IMAGE_RATIOS: [number, MediaPlacement, string][] = [
  [1 / 1, 'feed_square', '1:1'],
  [4 / 5, 'feed_portrait', '4:5'],
  [9 / 16, 'story', '9:16'],
]

// Meta states a 3% tolerance on image aspect ratio. Applied proportionally to the target
// ratio rather than as a fixed offset, so 9:16 is not held to a tighter bar than 1:1.
export const IMAGE_RATIO_TOLERANCE = 0.03

// Meta's own floor is 600px, but a 600px-wide image is visibly soft in a modern feed and
// these ads carry logos and salary text that have to stay legible. 1080 keeps the quality
// bar the old exact-size list implied.
export const IMAGE_MIN_WIDTH = 1080

// Accepted video aspect ratios: [ratio, placement, label]. Unlike images, Meta specifies
// video by aspect ratio rather than exact pixel size, so an upload is matched on ratio
// and only has to clear a minimum width.
export const ACCEPTED_VIDEO_RATIOS: [number, MediaPlacement, string][] = [
  [1 / 1, 'feed_square', '1:1'],
  [4 / 5, 'feed_portrait', '4:5'],
  [9 / 16, 'story', '9:16'],
]

// Ratios are matched with tolerance: real exports are rarely exact (a 600x1068 clip is
// 0.5618, not 0.5625), and Meta accepts the same slack.
export const VIDEO_RATIO_TOLERANCE = 0.02
export const VIDEO_MIN_WIDTH = 500

// Meta rejects an ad video shorter than 4s with an opaque HTTP 500 ("An unknown error
// has occurred"), so the limit is enforced here where it can still be explained.
export const VIDEO_MIN_DURATION_SECONDS = 4

export interface SocialMediaAsset {
  id: string
  name: string
  type: 'image' | 'video'
  placement?: MediaPlacement
  image?: { url: string; width?: number; height?: number }
  file?: { url: string; filename: string }
}

export interface DraftIds {
  smId: string | null
  campaignId: string | null
  adSetId: string | null
  leadFormId: string | null
  creativeId: string | null
  adId: string | null
}

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  FULL_NAME: 'Full Name',
  EMAIL: 'Email',
  PHONE_NUMBER: 'Phone Number',
  CITY: 'City',
  STATE: 'State',
  JOB_TITLE: 'Job Title',
  COMPANY_NAME: 'Company Name',
  JOB_ROLE: 'Job Role',
  CUSTOM: 'Custom Question',
}

export const PREDEFINED_QUESTION_KEYS: Record<Exclude<QuestionType, 'CUSTOM'>, string> = {
  FULL_NAME: 'full_name',
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
  CITY: 'city',
  STATE: 'state',
  JOB_TITLE: 'job_title',
  COMPANY_NAME: 'company_name',
  JOB_ROLE: 'job_role',
}
